import * as Graph from '../graph';
import * as OpKinds from '../opKinds';
import {hash} from '../hash';
import {toGqlQuery, gqlArgs, gqlObjectField} from './gql';
import {typedDict, Type, TypedDictType} from '../model/types';

const entityNameArgDescription = `The name of a ${Graph.docType(
  'user'
)} or ${Graph.docType('org')}`;
const projectNameArgDescription = `The name of the ${Graph.docType('project')}`;
const artifactTypeNameArgDescription = `The type name of the ${Graph.docType(
  'artifactType'
)}`;
const artifactVersionNameArgDescription = `The version name of the ${Graph.docType(
  'artifact'
)}: \`<ArtifactName>:<version>\``;
const orgNameArgDescription = `The name of the ${Graph.docType('org')}`;
const userNameArgDescription = `The name of the ${Graph.docType('user')}`;

// Note, these are all hidden for now! But we do use opRootArtifact
// to fetch tables now, so users do interact with these.
export const opRootEntity = OpKinds.makeTaggingStandardOp({
  hidden: true,
  name: 'root-entity',
  argTypes: {entityName: 'string'},
  description: `Directly fetch a ${Graph.docType('entity')} by name`,
  argDescriptions: {
    entityName: entityNameArgDescription,
  },
  returnValueDescription: `A ${Graph.docType('entity')}`,
  returnType: () => 'entity',
  resolver: async (
    inputs,
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    const alias = `entity_${hash(inputs.entityName)}`;
    const out = result[alias];
    return out;
  },
});

export const opRootProject = OpKinds.makeTaggingStandardOp({
  // hidden: true,
  name: 'root-project',
  argTypes: {entityName: 'string', projectName: 'string'},
  description: `Directly fetch a ${Graph.docType('project')}`,
  argDescriptions: {
    entityName: entityNameArgDescription,
    projectName: projectNameArgDescription,
  },
  returnValueDescription: `A ${Graph.docType('project')}`,
  returnType: () => 'project',
  renderInfo: {type: 'function'},
  resolver: async (
    inputs,
    inputTypes,
    rawInputs,
    forwardGraph,
    forwardOp,
    context
  ) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    const alias = `project_${hash(
      inputs.entityName + '/' + inputs.projectName
    )}`;
    return result[alias];
  },
});

export const opRootArtifactVersion = Graph.makeOp({
  hidden: true,
  name: 'root-artifactVersion',
  argTypes: {
    entityName: 'string',
    projectName: 'string',
    artifactTypeName: 'string',
    artifactVersionName: 'string',
  },
  description: `Directly fetch a ${Graph.docType('artifactVersion')}`,
  argDescriptions: {
    entityName: entityNameArgDescription,
    projectName: projectNameArgDescription,
    artifactTypeName: artifactTypeNameArgDescription,
    artifactVersionName: artifactVersionNameArgDescription,
  },
  returnValueDescription: `A ${Graph.docType('artifactVersion')}`,
  returnType: 'artifactVersion',
  renderInfo: {type: 'function'},
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    const projectAlias = `project_${hash(
      inputs.entityName + '/' + inputs.projectName
    )}`;
    const artifactTypeAlias = `artifactType_${hash(inputs.artifactTypeName)}`;
    const artifactAlias = `artifact_${hash(inputs.artifactVersionName)}`;
    return result?.[projectAlias]?.[artifactTypeAlias]?.[artifactAlias];
  },
});

export const opRootOrg = Graph.makeOp({
  hidden: true,
  name: 'root-org',
  argTypes: {orgName: 'string'},
  description: `Directly fetch a ${Graph.docType('org')}`,
  argDescriptions: {
    orgName: orgNameArgDescription,
  },
  returnValueDescription: `A ${Graph.docType('org')}`,
  returnType: 'org',
  renderInfo: {type: 'function'},
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    const alias = `organization_${hash(inputs.orgName)}`;
    return result[alias];
  },
});

export const opRootUser = Graph.makeOp({
  hidden: true,
  name: 'root-user',
  argTypes: {userName: 'string'},
  description: `Directly fetch a ${Graph.docType('user')}`,
  argDescriptions: {
    userName: userNameArgDescription,
  },
  returnValueDescription: `A ${Graph.docType('user')}`,
  returnType: 'user',
  renderInfo: {type: 'function'},
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    const alias = `user_${hash(inputs.userName)}`;
    return result[alias];
  },
});

// I don't think we should expose this, it would allow users to make
// queries that depend based on who is looking. But we can use it to suggest
// potentially useful things for the current viewer in suggest.ts
export const opRootViewer = Graph.makeOp({
  hidden: true,
  name: 'root-viewer',
  argTypes: {},
  description: `Directly fetch the current ${Graph.docType('user')}`,
  argDescriptions: {},
  returnValueDescription: `Current ${Graph.docType('user')}`,
  returnType: 'user',
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    return result.viewer;
  },
});

export const opRootFeaturedReports = Graph.makeOp({
  hidden: true,
  name: 'root-featuredreports',
  argTypes: {},
  returnType: {type: 'list', objectType: 'report'},
  renderInfo: {type: 'function'},
  description: `Directly fetch the featured ${Graph.docType('report', {
    plural: true,
  })}`,
  argDescriptions: {},
  returnValueDescription: `A ${Graph.docType(
    'list'
  )} of featured ${Graph.docType('report', {plural: true})}`,
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const featuredReportsQuery = {
      queryFields: [
        {
          name: 'featuredReports',
          fields: [
            {name: 'id', fields: []},
            {name: 'spec', fields: []},
          ],
        },
      ],
    };
    const featuredReportsResult = await context.backend.execute(
      featuredReportsQuery
    );
    const parsed = JSON.parse(featuredReportsResult.featuredReports.spec);
    const reportIds = parsed.reports.map((report: any) => report.id);
    const query = {
      queryFields: [
        {
          name: 'views',
          args: gqlArgs({ids: reportIds}),
          fields: [
            {
              name: 'edges',
              fields: [gqlObjectField(forwardGraph, forwardOp, 'node')],
            },
          ],
        },
      ],
    };
    const result = await context.backend.execute(query);
    return result.views.edges.map((edge: any) => edge.node);
  },
});

// Instance-wide root queries (hidden; for local activity dashboard only)
export const opRootAllProjects = Graph.makeOp({
  hidden: true,
  name: 'root-allProjects',
  argTypes: {},
  returnType: {type: 'list', objectType: 'project'},
  renderInfo: {type: 'function'},
  description: `Directly fetch all visible ${Graph.docType('project', {
    plural: true,
  })} - only available in local W&B deployments`,
  argDescriptions: {},
  returnValueDescription: `A ${Graph.docType(
    'list'
  )} of all visible ${Graph.docType('project', {plural: true})}`,
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    return result?.instance?.projects.edges.map((edge: any) => edge.node);
  },
});

export const opRootAllReports = Graph.makeOp({
  hidden: true,
  name: 'root-allReports',
  argTypes: {},
  returnType: {type: 'list', objectType: 'report'},
  renderInfo: {type: 'function'},
  description: `Directly fetch all ${Graph.docType('report', {
    plural: true,
  })} - only available in local W&B deployments`,
  argDescriptions: {},
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'report',
    {plural: true}
  )}`,
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    return result?.instance?.views.edges
      .filter((edge: any) => edge.node.type === 'runs')
      .map((edge: any) => edge.node);
  },
});

export const opRootAllArtifacts = Graph.makeOp({
  hidden: true,
  name: 'root-allArtifacts',
  argTypes: {},
  returnType: {type: 'list', objectType: 'artifact'},
  renderInfo: {type: 'function'},
  description: `Directly fetch all ${Graph.docType('artifact', {
    plural: true,
  })} - only available in local W&B deployments`,
  argDescriptions: {},
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'artifact',
    {plural: true}
  )}`,
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    return result?.instance?.artifactSequences.edges.map((e: any) => e.node);
  },
});

export const opRootAllEntities = Graph.makeOp({
  hidden: true,
  name: 'root-allEntities',
  argTypes: {},
  returnType: {type: 'list', objectType: 'entity'},
  renderInfo: {type: 'function'},
  description: `Directly fetch all ${Graph.docType('entity', {
    plural: true,
  })} - only available in local W&B deployments`,
  argDescriptions: {},
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'entity',
    {plural: true}
  )}`,
  resolver: async (inputs, forwardGraph, forwardOp, context) => {
    const query = {
      queryFields: toGqlQuery(forwardGraph, forwardOp),
    };
    const result = await context.backend.execute(query);
    return result?.instance?.entities;
  },
});

export const repoInsightsRowTypes = {
  rpt_weekly_users_by_country_by_repo: typedDict({
    user_fraction: 'number' as const,
    country: 'string' as const,
    created_week: 'date' as const,
    framework: 'string' as const,
  }),
  rpt_weekly_repo_users_by_persona: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    persona: 'string' as const,
    percentage: 'number' as const,
  }),
  rpt_weekly_engaged_user_count_by_repo: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    user_count: 'number' as const,
  }),
  rpt_repo_gpu_backends: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    gpu: 'string' as const,
    percentage: 'number' as const,
  }),
  rpt_versus_other_repos: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    percentage: 'number' as const,
  }),
  rpt_runtime_buckets: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    bucket: 'string' as const,
    bucket_run_percentage: 'number' as const,
  }),
  rpt_user_model_train_freq: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    train_freq: 'string' as const,
    percentage: 'number' as const,
  }),
  rpt_runs_versus_other_repos: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    percentage: 'number' as const,
  }),
  rpt_product_usage: typedDict({
    created_week: 'date' as const,
    framework: 'string' as const,
    product: 'string' as const,
    percentage: 'number' as const,
  }),
};

const makeRepoInsightsOp = (plotName: string, outputRowType: TypedDictType) => {
  return Graph.makeOp({
    hidden: true,
    name: plotName,
    argTypes: {
      repoName: 'string',
    },
    description: `Fetch the data needed to render the ${plotName} plot for a given repo`,
    argDescriptions: {
      repoName: `The name of the repo to fetch data for`,
    },
    returnValueDescription: ``,
    returnType: typedDict({
      rows: {type: 'list', objectType: outputRowType},
      isNormalizedUserCount: 'boolean',
    }),
    renderInfo: {type: 'function'},
    resolver: async (inputs, forwardGraph, forwardOp, context) => {
      const query = {
        queryFields: toGqlQuery(forwardGraph, forwardOp),
      };

      const result = await context.backend.execute(query);
      const alias = `repoInsights_${hash(inputs.repoName)}_${hash(plotName)}`;

      const schema: Array<{
        Name: string;
        Repeated: boolean;
        Required: boolean;
        Type: string;
      }> = result[alias]?.schema;
      if (!schema) {
        throw new Error(`No schema for ${alias}`);
      }

      const processRow = (row: any[]): {[key: string]: any} => {
        const processedRow: {[key: string]: any} = {};
        for (let i = 0; i < schema.length; i++) {
          // TODO(dag): Handle repeated and required
          const {Name: name, Type: type} = schema[i];
          if (type === 'TIMESTAMP') {
            processedRow[name] = new Date(row[i]);
          } else {
            processedRow[name] = row[i];
          }
        }
        return processedRow;
      };

      const rows = result[alias]?.edges.map((e: any) => processRow(e.node.row));

      const isNormalizedUserCount = result[alias]?.isNormalizedUserCount;
      return {rows, isNormalizedUserCount};
    },
  });
};

export const opRootRepoInsightsUsersByCountry = makeRepoInsightsOp(
  'rpt_weekly_users_by_country_by_repo',
  repoInsightsRowTypes.rpt_weekly_users_by_country_by_repo
);

export const opRootRepoInsightsUsersByPersona = makeRepoInsightsOp(
  'rpt_weekly_repo_users_by_persona',
  repoInsightsRowTypes.rpt_weekly_repo_users_by_persona
);

export const opRootRepoInsightsWeeklyEngagedUserCount = makeRepoInsightsOp(
  'rpt_weekly_engaged_user_count_by_repo',
  repoInsightsRowTypes.rpt_weekly_engaged_user_count_by_repo
);

export const opRootRepoInsightsGpuBackend = makeRepoInsightsOp(
  'rpt_repo_gpu_backends',
  repoInsightsRowTypes.rpt_repo_gpu_backends
);

export const opRootRepoInsightsVersusOtherRepos = makeRepoInsightsOp(
  'rpt_versus_other_repos',
  repoInsightsRowTypes.rpt_versus_other_repos
);

export const opRootRepoInsightsWeeklyRuntimeBuckets = makeRepoInsightsOp(
  'rpt_runtime_buckets',
  repoInsightsRowTypes.rpt_runtime_buckets
);

export const opRootRepoInsightsWeeklyModelTrainFreq = makeRepoInsightsOp(
  'rpt_user_model_train_freq',
  repoInsightsRowTypes.rpt_user_model_train_freq
);

export const opRootRepoInsightsRunsVersusOtherRepos = makeRepoInsightsOp(
  'rpt_runs_versus_other_repos',
  repoInsightsRowTypes.rpt_runs_versus_other_repos
);

export const opRootRepoInsightsProductUsage = makeRepoInsightsOp(
  'rpt_product_usage',
  repoInsightsRowTypes.rpt_product_usage
);
